import React, { useState, useEffect } from "react";

interface Option {
  name: string;
  description: string;
}

interface SwitchTabProps {
  options: Option[];
  defaultValue?: string; // Initially selected "name"
  onChange?: (selectedName: string) => void;
  className?: string;
}

/**
 * A segmented tab control that:
 * - Displays a row of "pills" for each option's `name`.
 * - Highlights the active pill.
 * - Shows the `description` of the selected option.
 * - Notifies parent when selection changes via `onChange`.
 */
const SwitchTab: React.FC<SwitchTabProps> = ({
  options,
  defaultValue,
  onChange,
  className = "",
}) => {
  // Initialize selected name
  const [selectedName, setSelectedName] = useState<string>(() => {
    if (defaultValue) return defaultValue;
    return options.length ? options[0].name : "";
  });

  useEffect(() => {
    if (defaultValue !== undefined) {
      setSelectedName(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (onChange) {
      onChange(selectedName);
    }
  }, [selectedName, onChange]);

  if (!options.length) return null;

  // The selected option for showing its description
  const activeOption = options.find((opt) => opt.name === selectedName);

  return (
    <div className={`SwitchTab flex flex-col items-center gap-8 ${className}`}>
      {/* Pills */}
      <div className="p-1 rounded-full border border-[#d0d2cc] flex gap-1">
        {options.map((option) => {
          const isActive = option.name === selectedName;
          return (
            <div
              key={option.name}
              className={`px-6 py-4 rounded-full cursor-pointer ${
                isActive ? "bg-[#132527]" : ""
              }`}
              onClick={() => setSelectedName(option.name)}
            >
              <div
                className={`text-base font-semibold font-['Manrope'] ${
                  isActive ? "text-[#fcfbf8]" : "text-[#717c79]"
                }`}
              >
                {option.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Description */}
      {activeOption && (
        <div className="w-[960px] text-center text-[#36403f] text-lg font-medium leading-loose font-['Manrope']">
          {activeOption.description}
        </div>
      )}
    </div>
  );
};

export default SwitchTab;
